import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth';
import router from '@/router';
import { message } from 'ant-design-vue';

/**
 * API client instance with auth interceptors
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: Add JWT token and trace ID to headers
apiClient.interceptors.request.use(
  (config) => {
    // Add trace ID from window (set by useTraceId composable)
    const traceId = (window as any).__HYPERKE_TRACE_ID || crypto.randomUUID();
    config.headers['X-Request-ID'] = traceId;
    
    // Always check localStorage first (most reliable source of truth)
    const storedToken = localStorage.getItem('token');
    const authStore = useAuthStore();
    const storeToken = authStore.token;
    
    // Debug logging
    console.log('[API Client] Request interceptor:', {
      url: config.url,
      method: config.method,
      traceId,
      hasStoredToken: !!storedToken,
      hasStoreToken: !!storeToken,
      storedTokenLength: storedToken?.length || 0,
      storeTokenLength: storeToken?.length || 0,
      tokensMatch: storedToken === storeToken,
    });
    
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
      console.log('[API Client] Using token from localStorage, header set:', {
        headerLength: config.headers.Authorization?.length || 0,
        tokenPrefix: storedToken.substring(0, 20) + '...',
      });
      // Sync with store
      if (authStore.token !== storedToken) {
        console.log('[API Client] Syncing store with localStorage token');
        authStore.token = storedToken;
      }
    } else if (storeToken) {
      config.headers.Authorization = `Bearer ${storeToken}`;
      console.log('[API Client] Using token from store (localStorage empty), header set:', {
        headerLength: config.headers.Authorization?.length || 0,
        tokenPrefix: storeToken.substring(0, 20) + '...',
      });
    } else {
      console.warn('[API Client] No token available for request:', config.url);
    }

    return config;
  },
  (error) => {
    console.error('[API Client] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor: Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Extract request ID from response headers for correlation
    const requestId = response.headers['x-request-id'] || 'unknown';
    if (import.meta.env.DEV) {
      console.log('[API Client] Response success:', {
        url: response.config.url,
        status: response.status,
        requestId,
      });
    }
    return response;
  },
  (error: AxiosError) => {
    // Fix: Silently ignore canceled requests (expected behavior when switching tabs/routes)
    // Axios uses 'canceled' message for aborted requests
    if (error.message === 'canceled' || error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
      // Don't log canceled requests - they're expected when aborting previous requests
      return Promise.reject(error);
    }

    const requestId = error.response?.headers['x-request-id'] || 'unknown';
    const status = error.response?.status;
    const data = error.response?.data as any;
    const url = error.config?.url;

    console.error('[API Client] Response error:', {
      url,
      method: error.config?.method,
      status,
      statusText: error.response?.statusText,
      message: error.message,
      requestId,
      // Don't log sensitive data
      responseData: error.response?.data,
    });

    // Handle 401 Unauthorized - logout and redirect
    if (status === 401) {
      console.error('[API Client] 401 Unauthorized - logging out:', {
        url,
        requestId,
        responseMessage: data?.message,
      });
      const authStore = useAuthStore();
      authStore.logout(); // This already handles redirect via window.location.href
      message.error('Session expired. Please login again.');
      // Don't call router.push here - logout() already handles redirect
      return Promise.reject(error);
    }

    // Handle 403 Forbidden - permission denied
    if (status === 403) {
      message.error('You do not have permission to perform this action.');
      return Promise.reject(error);
    }

    // Handle 404 Not Found - resource missing
    if (status === 404) {
      message.error('The requested resource was not found.');
      return Promise.reject(error);
    }

    // Handle 409 Conflict - duplicate resource
    if (status === 409) {
      message.error(data?.message || 'This resource already exists.');
      return Promise.reject(error);
    }

    // Handle 422 Validation Error - invalid input
    if (status === 422) {
      message.error(data?.message || 'Invalid data provided.');
      return Promise.reject(error);
    }

    // Handle 5xx Server Errors
    if (status && status >= 500) {
      message.error('Server error. Please try again later.');
      return Promise.reject(error);
    }

    // Handle errors with message property (validation, client errors)
    if (data && typeof data === 'object' && 'message' in data) {
      message.error(data.message || 'Request failed');
      return Promise.reject(error);
    }

    // Handle network errors (no response)
    if (!status) {
      message.error('Network error. Please check your connection.');
      return Promise.reject(error);
    }

    // Unknown error - fallback
    message.error('Request failed. Please try again.');
    return Promise.reject(error);
  }
);

export default apiClient;